/**
 * Clear schedule actions with tipster menu.
 * Provides options to clear scheduled matches for a day, all days, or including completed.
 */
import { mutationRequest } from 'services/mutation/mutationRequest';
import { competitionEngine } from 'tods-competition-factory';
import { tipster } from 'components/popovers/tipster';
import { isFunction } from 'functions/typeOf';
import { t } from 'i18n';

import { BULK_SCHEDULE_MATCHUPS } from 'constants/mutationConstants';
import { BOTTOM } from 'constants/tmxConstants';

type ClearScheduleParams = {
  scheduledDate: string;
  target: HTMLElement;
  roundNameFilter?: string;
  eventIdFilter?: string;
  callback?: () => void;
};

export function clearSchedule({
  scheduledDate,
  target,
  roundNameFilter,
  eventIdFilter,
  callback,
}: ClearScheduleParams): void {
  const result = competitionEngine.competitionScheduleMatchUps({ courtCompletedMatchUps: true });
  const { dateMatchUps = [], completedMatchUps = [] } = result;
  const matchUps = dateMatchUps.concat(...completedMatchUps);
  const scheduledMatchUps = matchUps.filter(
    ({ schedule }: any) => schedule.scheduledDate || schedule.scheduledTime || schedule.courtId,
  );
  const selectedDateMatchUps = scheduledMatchUps.filter(
    ({ schedule }: any) => schedule.scheduledDate === scheduledDate,
  );

  const options = [
    {
      disabled: !selectedDateMatchUps.length,
      option: t('pages.schedule.clearScheduledMatches'),
      onClick: clearScheduleDay,
    },
    {
      disabled: !selectedDateMatchUps.length,
      option: t('pages.schedule.clearAllMatches'),
      onClick: clearCompleted,
    },
    {
      disabled: !scheduledMatchUps.length,
      option: t('pages.schedule.clearAllDays'),
      onClick: resetSchedule,
    },
  ];

  if (options.filter((o) => !o.disabled).length) {
    tipster({ target, options, config: { placement: BOTTOM } });
  }

  function resetSchedule() {
    clearScheduleDay({ all: true });
  }
  function clearCompleted() {
    clearScheduleDay({ completed: true });
  }
  function clearScheduleDay({ all, completed }: { all?: boolean; completed?: boolean } = {}) {
    let filteredMatchUps = scheduledMatchUps
      .filter(({ schedule }: any) => schedule.scheduledDate === scheduledDate)
      .filter(({ roundName }: any) => !roundNameFilter || roundNameFilter === roundName)
      .filter(({ eventId }: any) => !eventIdFilter || eventIdFilter === eventId);

    if (!completed) {
      filteredMatchUps = filteredMatchUps?.filter(
        ({ matchUpStatus, winningSide }: any) => matchUpStatus !== 'COMPLETED' && !winningSide,
      );
    }

    const toBeCleared = all ? scheduledMatchUps : filteredMatchUps;
    const matchUpIds = toBeCleared.map((m: any) => m.matchUpId);

    const methods = [
      {
        method: BULK_SCHEDULE_MATCHUPS,
        params: {
          schedule: { courtId: '', scheduledDate: '', courtOrder: '', scheduledTime: '', venueId: '' },
          removePriorValues: true,
          matchUpIds,
        },
      },
    ];

    const postMutation = (result: any) => {
      if (result.success && callback && isFunction(callback)) {
        callback();
      }
    };
    mutationRequest({ methods, callback: postMutation });
  }
}
